import { CONSTANTS } from '@pk/powerkioskutils';

import _ from 'lodash';
import moment from 'moment-timezone';

import { HelperService } from '../helper.service';
import { AccountAdjustment } from './account-adjustment';
import { AccountManager } from './account-manager';
import { AgentAllowedCommissionPayPlanType } from './agent-allowed-commission-pay-plan-type';
import { AgentBiddingStats } from './agent-bidding-stats';
import { AgentDiscountProgram } from './agent-discount-program';
import { AgentIsEmployee } from './agent-is-employee';
import { AgentRateMatrix } from './agent-rate-matrix';
import { AgentRemainingBalance } from './agent-remaining-balance';
import { AgentSalesAgent } from './agent-sales-agent';
import { AgentStripeCustomer } from './agent-stripe-customer';
import { AgentTheme } from './agent-theme';
import { AgentUserNotificationCategoryRecipient } from './agent-user-notification-category-recipient';
import { Attachment } from './attachment';
import { BankAccount } from './bank-account';
import { BusinessEntityType } from './business-entity-type';
import { BusinessEntityTypeTaxClassification } from './business-entity-type-tax-classification';
import { CommissionUpfrontRate } from './commission-upfront-rate';
import { CommissionUpfrontRateLevel } from './commission-upfront-rate-level';
import { Company } from './company';
import { Country } from './country';
import { ECommerceWebsite } from './e-commerce-website';
import { LicensePlan } from './license-plan';
import { MailServer } from './mail-server';
import { Note } from './note';
import { PromoCode } from './promo-code';
import { State } from './state';
import { StripeCard } from './stripe';
import { Supplier } from './supplier';
import { Tag } from './tag';
import { Task } from './task';
import { User } from './user';
import { UserNotificationCategory } from './user-notification-category';
import { ViewAgentCommissionPayPlanOption } from './view-agent-commission-pay-plan-option';

export class Agent {
	public agentId: string;
	public parentId: string;
	public brotherId: string;
	public name: string;
	public address1: string;
	public address2: string;
	public city: string;
	public stateId: string;
	public state: State;
	public zipCode: string;
	public phone: string;
	public mobile: string;
	public phone2: string;
	public phoneExt: string;
	public fax: string;
	public technicalEmail: string;
	public supportEmail: string;
	public addDate: Date;
	public isActive: boolean;
	public isFraudulent: boolean;
	public countryId: number;
	public phoneCountryId: number;
	public phone2CountryId: number;
	public faxCountryId: number;
	public mobileCountryId: number;
	public stateOther: string;
	public useDefaultMailServer: boolean;
	public useReports: boolean;
	public usePayableReports: boolean;
	public useSupportSystem: boolean;
	public mailServerId: string;
	public ssn: string;
	public legalTaxName: string;
	public businessTaxName: string;
	public commissionRate: number;
	public commissionRate2: number;
	public commissionRate3: number;
	public residentialCommissionRate: number;
	public residentialCommissionRate2: number;
	public residentialCommissionRate3: number;
	public discountFactor: number;
	public customerCompany: string;
	public supplierCompany: string;
	public agentDomainAlias: string;
	public eCommerceDomainName: string;
	public businessEntityTypeId: number;
	public businessEntityTypeTaxClassificationId: number;
	public underwriterId: string;
	public excludedSuppliers: Supplier[];
	public isPaidIndividually: boolean;
	public showEducation: boolean;
	public isEmployee: boolean;
	public userId: string;
	public promoCodeCount: number;
	public oldPromoCodeCount: number;
	public agentBundleCount: number;
	public oldAgentBundleCount: number;
	public licensePlanId: number;
	public agentOrParentOrBrotherId: string;
	public isOnboarded: boolean;
	public hasParent: boolean;
	public onboardDate: Date;
	public volume: number;
	public id: string;
	public isEnterprise: boolean;
	public hasFailedPayment: boolean;
	public paymentIntentRequiresActionDate: Date;
	public electricityResidentialThankYouLink: string;
	public gasResidentialThankYouLink: string;
	public isInHouseIndirectSale: boolean;
	public commissionAmountYTD: number;
	public futureCommissionAmount: number;
	public totalUpfrontCommissionPaidAmount: number;
	public flowedTermValue: number;
	public showUtilityCheck: boolean;
	public negativeBalance: number;
	public reversibleCommission: number;
	public currMonthEstimatedCommission: number;
	public lastMonthPaidCommission: number;
	public allowAutomaticResidentialConfirmation: boolean;
	public isDefaultInHouseIndirectSale: boolean;
	public isVerified: boolean;
	public residentialContractRatio: number;
	public termsOfServiceAttachmentId: string;
	public coverPageAttachmentId: string;
	public showFinancialProfile: boolean;
	public holdPayables: boolean;
	public holdEnrollments: boolean;
	public idDocumentAttachmentId: string;
	public isCommissionPaused: boolean;
	public pauseCommissionReasonNoteTypeId: string;
	public pauseCommissionReasonNoteBody: string;
	public isBundledAgent: boolean;
	public companyId: string;
	public accountManagerId: number;
	public includeBufferMargin: boolean;
	public includeBufferMarginForAllSubAgents: boolean;
	public isNewBroker: boolean;
	public commissionPayPlanTypeId: number;
	public marketOpportunitiesAlertEffectiveDate: Date;
	public dropRatio: number;
	public droppedTermValue: number;
	public rejectedTermValue: number;
	public maxReversibleCommission: number;
	public maxReversibleCommissionOverride: number;
	public hasConfirmedContracts: boolean;
	public overrideCommissionPayPlanTypeId: number;
	public commissionSplitTypeId: number;
	public commissionSplitElectricityBufferMarginParent: number;
	public commissionSplitGasBufferMarginParent: number;
	public commissionSplitElectricityBufferMarginBrother: number;
	public commissionSplitGasBufferMarginBrother: number;
	public showCapSplitWarning: boolean;
	public webHookUrl: string;
	public webHookSecret: string;
	public darkBackgroundsLogoAttachmentId: string;
	public lightBackgroundsLogoAttachmentId: string;
	public isHighImpactAgent: boolean;
	public matrixElectricityBufferMargin: number;
	public matrixGasBufferMargin: number;
	public matrixElectricityBufferMarginStaging: number;
	public matrixGasBufferMarginStaging: number;
	public auctionElectricityBufferMargin: number;
	public auctionGasBufferMargin: number;
	public auctionElectricityBufferMarginStaging: number;
	public auctionGasBufferMarginStaging: number;
	public isHighImpactAgentOverride: boolean;
	public useAgentDiscountProgram: boolean;
	public hasUnlimitedSalesCodes: boolean;
	public hasResidentialCustomerPoa: boolean;
	public commissionSplitBufferMarginCommissionRate: number;
	public commissionSplitBufferMarginCommissionRate2: number;
	public commissionSplitBufferMarginCommissionRate3: number;
	public complianceDropPredictionThreshold: number;
	public w9AttachmentId: string;
	public showCustomerCrm: string;
	public showBrokerCrm: string;
	public quickPayRate: number;
	public quickPayRate2: number;
	public quickPayRate3: number;
	public matrixElectricityMarginCap: number;
	public matrixGasMarginCap: number;
	public useResidualCommissionRate: boolean;
	public residualCommissionRate: number;
	public commissionPayoutDate: number;
	public maxMidMonthPayPayment: number;
	public maxQuickPayLag: number;
	public maxUpfrontAmount: number;
	public allowNonResidualPayablesOnResidualReceivablePlans: boolean;
	public useDynamicCommissionOptionBufferMargin: boolean;
	public applyCommissionOptionBufferMargin: boolean;
	public residentialPoaAttachmentId: string;
	public residentialTosAttachmentId: string;
	public useStaticCommissionRate: boolean;
	public residualToUpfrontMatrixElectricityBufferMargin: number;
	public residualToUpfrontMatrixGasBufferMargin: number;
	public residualToUpfrontAuctionElectricityBufferMargin: number;
	public residualToUpfrontAuctionGasBufferMargin: number;
	public addFutureNegativeBalance: number;
	public addFutureNegativeBalanceDate: Date | string;
	public showNewYorkCommercialMatrix: boolean;
	public showQuoteForecastedCommission: boolean;
	public sapExternalId: string;
	public sapExternalAgentId: string;
	public commissionRegistrationId: string;
	public minShortTermPackingPercent: number;
	public maxShortTermPackingPercent: number;
	public showEnergyPortal: boolean;
	public electricityMarginSelectionMax: number;
	public gasMarginSelectionMax: number;
	public targetEnergyPortalBudgetMonth: number;
	public qboVendorId: number;

	public selectedForBundle: boolean;
	public selectedForUnbundle: boolean;
	public isSaas: boolean;
	public displayName: string;
	public extendMarketOpportunitiesAlert: boolean;
	public showLicenses: boolean;
	public tagsByCategory: { [key: number]: Tag[] };

	public darkDisplayLogoAttachment: Attachment; // graphql alias property
	public lightDisplayLogoAttachment: Attachment; // graphql alias property

	public parent: Agent;
	public brother: Agent;
	public country: Country;
	public phoneCountry: Country;
	public phone2Country: Country;
	public mobileCountry: Country;
	public faxCountry: Country;
	public mailServer: MailServer;
	public eCommerceWebsites: ECommerceWebsite[];
	public attachments: Attachment[];
	public tasks: Task[];
	public rateMatrixes: AgentRateMatrix[];
	public promoCodes: PromoCode[];
	public licensePlans: LicensePlan[];
	public businessEntityType: BusinessEntityType;
	public businessEntityTypeTaxClassification: BusinessEntityTypeTaxClassification;
	public underwriter: User;
	public bankAccounts: BankAccount[];
	public logins: User[];
	public subAgents: Agent[];
	public brothers: Agent[];
	public addOns: LicensePlan[];
	public discountProgram: AgentDiscountProgram;
	public stats: AgentBiddingStats;
	public userNotificationCategories: UserNotificationCategory[];
	public userNotificationCategoryRecipients: AgentUserNotificationCategoryRecipient[];
	public parentIsEmployee: AgentIsEmployee;
	public brotherIsEmployee: AgentIsEmployee;
	public commissionUpfrontRateLevels: CommissionUpfrontRateLevel[];
	public commissionUpfrontRates: CommissionUpfrontRate[];
	public customerCards: StripeCard[];
	public company: Company;
	public accountManager: AccountManager;
	public marketOpportunities: { utilityName: string; stateShort: string }[];
	public agentStripeCustomer: AgentStripeCustomer;
	public commissionPayPlanOptionStats: { description: string; maxRate: number }[];
	public pauseCommissionReason: { noteId: string; noteTypeId: string; subject: string; body: string };
	public pauseCommissionReasonNote: Note;
	public darkBackgroundsLogoAttachment: Attachment;
	public lightBackgroundsLogoAttachment: Attachment;
	public faviconAttachmentId: string;
	public ogImageAttachmentId: string;
	public themes: AgentTheme[];
	public agentAllowedCommissionPayPlanTypes: AgentAllowedCommissionPayPlanType[];
	public viewAgentCommissionPayPlanOptions: ViewAgentCommissionPayPlanOption[];
	public commissionCycleNegativeBalance: Agent;
	public commissionCycleAccountAdjustments: { currentCycle: AccountAdjustment[]; nextCycle: AccountAdjustment[] };
	public agentRemainingBalances: AgentRemainingBalance[];
	public agentMaxQuickPayPayment: number;
	public salesAgents: AgentSalesAgent[];
	public tags: Tag[];
	public showLayeredPurchasingBanner: boolean;
	public hasEnergyPortalAgents: boolean;

	get email(): string {
		const login = this.getLogin();

		return login?.email;
	}

	get contactFname(): string {
		const login = this.getLogin();

		return login?.fname;
	}

	get contactMiddle(): string {
		const login = this.getLogin();

		return login?.middle;
	}

	get contactLname(): string {
		const login = this.getLogin();

		return login?.lname;
	}

	get showPayableReports() {
		if (!this.parent) {
			return true;
		}

		return this.usePayableReports && this.parent.usePayableReports;
	}

	private platinumIds = CONSTANTS.licensePlans.platinumIds.concat(CONSTANTS.licensePlans.oldPlatinumIds);
	private newBusinessFullIds = this.platinumIds.concat(CONSTANTS.licensePlans.goldIds).concat(CONSTANTS.licensePlans.silverIds);
	private oldBusinessFullIds = CONSTANTS.licensePlans.oldPlatinumIds;
	private businessFullIds = this.newBusinessFullIds.concat(this.oldBusinessFullIds);
	private newSalesCodeBundleIds = CONSTANTS.licensePlans.salesCodeBundleIds;
	private oldSalesCodeBundleIds = CONSTANTS.licensePlans.oldSalesCodeBundleIds;
	private salesCodeBundleIds = this.oldSalesCodeBundleIds.concat(this.newSalesCodeBundleIds);
	private newAgentBundleIds = CONSTANTS.licensePlans.newAgentBundleIds;
	private oldAgentBundleIds = CONSTANTS.licensePlans.oldAgentBundleIds;
	private agentBundleIds = this.newAgentBundleIds.concat(this.oldAgentBundleIds);
	private oldTurnKeyIds = CONSTANTS.licensePlans.oldTurnKeyIds;
	private newTurnKeyIds = CONSTANTS.licensePlans.newTurnKeyIds;
	private turnkeyIds = this.oldTurnKeyIds.concat(this.newTurnKeyIds);
	private oldApiIds = CONSTANTS.licensePlans.oldApiIds;
	private newApiIds = CONSTANTS.licensePlans.newApiIds;
	private apiIds = this.oldApiIds.concat(this.newApiIds);
	private bundleIds = this.salesCodeBundleIds.concat(this.agentBundleIds);
	private newAffiliateIds = CONSTANTS.licensePlans.newAffiliateIds;
	private billReaderAIIds = CONSTANTS.licensePlans.billReaderAIIds;
	private businessLiteIds = CONSTANTS.licensePlans.businessLiteIds;

	constructor(agent?: Agent) {
		Object.assign(this, agent);

		this.addDate = this.addDate ? new Date(moment(this.addDate).format('MMM DD, YYYY hh:mm:ss a')) : this.addDate;
		this.addFutureNegativeBalanceDate = this.addFutureNegativeBalanceDate
			? new Date(moment(this.addFutureNegativeBalanceDate).format('YYYY-MM-DDT00:00:00'))
			: this.addFutureNegativeBalanceDate;
		this.parent = this.parent ? new Agent(this.parent) : this.parent;
		this.brother = this.brother ? new Agent(this.brother) : this.brother;
		this.country = this.country ? new Country(this.country) : this.country;
		this.faxCountry = this.faxCountry ? new Country(this.faxCountry) : this.faxCountry;
		this.phoneCountry = this.phoneCountry ? new Country(this.phoneCountry) : this.phoneCountry;
		this.mobileCountry = this.mobileCountry ? new Country(this.mobileCountry) : this.mobileCountry;
		this.logins = this.logins ? this.logins.map(u => new User(u)) : this.logins;
		this.underwriter = this.underwriter ? new User(this.underwriter) : this.underwriter;
		this.excludedSuppliers = this.excludedSuppliers ? this.excludedSuppliers.map(s => new Supplier(s)) : this.excludedSuppliers;
		this.subAgents = this.subAgents ? this.subAgents.map(a => new Agent(a)) : this.subAgents;
		this.brothers = this.brothers ? this.brothers.map(b => new Agent(b)) : this.brothers;
		this.promoCodes = this.promoCodes ? this.promoCodes.map(p => new PromoCode(p)) : this.promoCodes;
		this.licensePlans = this.licensePlans ? this.licensePlans.map(l => new LicensePlan(l)) : this.licensePlans;
		this.marketOpportunitiesAlertEffectiveDate = this.marketOpportunitiesAlertEffectiveDate
			? new Date(moment(this.marketOpportunitiesAlertEffectiveDate).format('MMM DD, YYYY hh:mm:ss a'))
			: this.marketOpportunitiesAlertEffectiveDate;
		this.company = this.company ? new Company(this.company) : this.company;
		this.paymentIntentRequiresActionDate = this.paymentIntentRequiresActionDate
			? new Date(moment(this.paymentIntentRequiresActionDate).format('MMM DD, YYYY hh:mm:ss a'))
			: this.paymentIntentRequiresActionDate;

		this.displayName = HelperService.showAsteriskForBrokerFirstImpression(this.isNewBroker) ? this.name + '*' : this.name;
	}

	get canShowNewYorkCommercialMatrix() {
		return this.parent?.showNewYorkCommercialMatrix ?? this.showNewYorkCommercialMatrix;
	}

	public getContactFullName(): string {
		const contactMiddle = this.contactMiddle && this.contactMiddle.trim() ? this.contactMiddle.trim() + ' ' : '';
		return this.contactFname ? `${this.contactFname} ${contactMiddle}${this.contactLname}` : '';
	}

	public getContactFullNameLastFirst(): string {
		return this.contactFname
			? `${this.contactLname}, ${this.contactFname} ${this.contactMiddle && this.contactMiddle.trim() ? ' ' + this.contactMiddle.trim() : ''}`
			: '';
	}

	public getFullAddressLine(): string {
		let addressLine = '';
		const stateOther = this.stateOther ? this.stateOther + ' ' : ' ';

		addressLine += this.address1 + ' ';
		addressLine += this.address2 ? this.address2 + ' ' : '';
		addressLine += this.city + ', ';
		addressLine += this.state && (!this.countryId || this.countryId === CONSTANTS.countries.unitedStates) ? this.state.stateShort + ' ' : stateOther;
		addressLine += this.zipCode;

		return addressLine;
	}

	public getLicensePlan(): LicensePlan {
		return this.licensePlans ? this.licensePlans.find(l => !l.parents?.length) : null;
	}

	get isBusinessFull(): boolean {
		const licensePlan = this.getLicensePlan();
		return !this.licensePlans || !this.licensePlans.length || (licensePlan && this.businessFullIds.includes(licensePlan.id));
	}

	public getAddOns(): LicensePlan[] {
		return this.licensePlans
			? this.licensePlans
					.filter(l => !!l.parents?.length && !this.bundleIds.includes(l.id))
					.concat(this.getSalesCodeBundles())
					.concat(this.getAgentBundles())
			: null;
	}

	public hasNoAgentBundle(): boolean {
		return !this.licensePlans.some(l => this.agentBundleIds.includes(l.id));
	}

	public hasSeparateAgentBundle(): boolean {
		return this.licensePlans.some(l => this.oldAgentBundleIds.includes(l.id));
	}

	public hasDefaultAgentBundle(): boolean {
		return this.licensePlans.some(l => this.newAgentBundleIds.includes(l.id));
	}

	public hasNoSalesCodeBundle(): boolean {
		return !this.licensePlans.some(l => this.salesCodeBundleIds.includes(l.id));
	}

	public hasSeparateSalesCodeBundle(): boolean {
		return this.licensePlans.some(l => this.oldSalesCodeBundleIds.includes(l.id));
	}

	public hasDefaultSalesCodeBundle(): boolean {
		return this.licensePlans.some(l => this.newSalesCodeBundleIds.includes(l.id));
	}

	public hasNoTurnkey(): boolean {
		return !this.licensePlans.some(l => this.turnkeyIds.includes(l.id));
	}

	public hasSeparateTurnkey(): boolean {
		return this.licensePlans.some(l => this.oldTurnKeyIds.includes(l.id));
	}

	public hasDefaultTurnkey(): boolean {
		return this.licensePlans.some(l => this.newTurnKeyIds.includes(l.id));
	}

	public hasNoAPI(): boolean {
		return !this.licensePlans.some(l => this.apiIds.includes(l.id));
	}

	public hasSeparateAPI(): boolean {
		return this.licensePlans.some(l => this.oldApiIds.includes(l.id));
	}

	public hasDefaultAPI(): boolean {
		return this.licensePlans.some(l => this.newApiIds.includes(l.id));
	}

	public hasDefaultAffiliatePlan(): boolean {
		return this.licensePlans.some(l => this.newAffiliateIds.includes(l.id));
	}

	public hasBillReaderAI(): boolean {
		return this.licensePlans?.some(l => this.billReaderAIIds.includes(l.id) || this.platinumIds.includes(l.id)) ?? false;
	}

	public hasSeparateNewBusinessFull(): boolean {
		return this.licensePlans.some(l => this.newBusinessFullIds.includes(l.id));
	}

	public hasSeparateOldBusinessFull(): boolean {
		return this.licensePlans.some(l => this.oldBusinessFullIds.includes(l.id));
	}

	public hasSeparateBusinessLite(): boolean {
		return this.licensePlans.some(l => this.businessLiteIds.includes(l.id));
	}

	public hasSeparateBusinessNoCharge(): boolean {
		return this.licensePlans.some(l => this.businessFullIds.includes(l.id) && l.upfrontFee === 0 && l.monthlyFee === 0);
	}

	public hasLicensePlan(licensePlanId: number): boolean {
		return this.licensePlans.some(l => l.id === licensePlanId);
	}

	private getSalesCodeBundles(): LicensePlan[] {
		const salesCodePackage = this.licensePlans.find(l => this.salesCodeBundleIds.includes(l.id));
		const salesCodeBundles = [];
		if (salesCodePackage) {
			for (let i = 0; i < this.promoCodeCount; i++) {
				salesCodeBundles.push(salesCodePackage);
			}
		}
		return salesCodeBundles;
	}

	private getAgentBundles(): LicensePlan[] {
		const agentBundle = this.licensePlans.find(l => this.agentBundleIds.includes(l.id));
		const agentBundles = [];
		if (agentBundle) {
			for (let i = 0; i < this.agentBundleCount; i++) {
				agentBundles.push(agentBundle);
			}
		}
		return agentBundles;
	}

	public getTotalCommissionRate(): number {
		// residualPercentage is always 85
		const value = (85 * this.commissionRate) / (this.commissionRate + this.commissionRate2 + this.commissionRate3);

		return Math.round(value);
	}

	public getCurrentLevelIcon(): string {
		return this.getLevelIcon(this.discountProgram);
	}

	public getLevelIcon(discountProgram: AgentDiscountProgram): string {
		if (!discountProgram) {
			return 'check-circle';
		}

		switch (discountProgram.id) {
			case 3: {
				return 'check-circle';
			}
			case 4:
			case 5:
			case 6: {
				return 'medal';
			}
			case 7: {
				return 'gem';
			}
			default: {
				return 'check-circle';
			}
		}
	}

	public getLogin(): User | null {
		if (this.logins) {
			const logins = _.orderBy(this.logins, ['isPrimary', 'addDate'], ['desc', 'asc']);

			return logins[0];
		}
	}
}
